import { useEffect, useRef, useState } from 'react';
import type { Highlight, HighlightMoveHandler } from '../types';
import { highlightToGeometry } from '../helpers/highlightToGeometry';

export interface MoveDragType {
    id: string;
    el: HTMLElement | SVGElement;
    type: 'positioned' | 'polygon' | 'rect';
    startNX: number;
    startNY: number;
    startLeft?: number;
    startTop?: number;
    origPoints?: Array<{ x: number; y: number }>;
    origX1?: number;
    origY1?: number;
    origX2?: number;
    origY2?: number;
}

export function useMoveDrag(
    activeMode: string | null,
    pdfLayersRef: React.RefObject<HTMLDivElement | null>,
    dimension: { width: number; height: number },
    pdfDocIdRef: React.MutableRefObject<string>,
    onSaveRef: React.MutableRefObject<((highlights: any[]) => void) | undefined> | undefined,
    setHighlights: React.Dispatch<React.SetStateAction<any[]>>,
    setActivePopup: React.Dispatch<React.SetStateAction<any>>,
    onHighlightMoveRef?: React.MutableRefObject<HighlightMoveHandler | undefined>,
    highlightsRef?: React.MutableRefObject<Highlight[]>,
) {
    const moveDragRef = useRef<MoveDragType | null>(null);
    const moveSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeModeRef = useRef(activeMode);
    activeModeRef.current = activeMode;

    useEffect(() => {
        if (activeMode !== 'move') {
            moveDragRef.current = null;
            document.body.style.cursor = '';
        }
    }, [activeMode]);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            const m = moveDragRef.current;
            if (!m) return;
            const layers = pdfLayersRef.current;
            if (!layers || !dimension.width || !dimension.height) return;
            const base = layers.getBoundingClientRect();
            const nx = (e.clientX - base.left) / dimension.width;
            const ny = (e.clientY - base.top) / dimension.height;
            const dx = nx - m.startNX;
            const dy = ny - m.startNY;
            const w = dimension.width;
            const h = dimension.height;

            if (m.type === 'positioned') {
                const el = m.el as HTMLElement;
                const newLeft = (m.startLeft ?? 0) + dx * w;
                const newTop = (m.startTop ?? 0) + dy * h;
                el.style.left = `${newLeft}px`;
                el.style.top = `${newTop}px`;
                const lbl = el.parentElement?.querySelector<HTMLElement>(`[data-label-for="${m.id}"]`);
                if (lbl) { lbl.style.left = `${newLeft + 12}px`; lbl.style.top = `${newTop - 8}px`; }
            } else if (m.type === 'polygon') {
                const newPts = (m.origPoints ?? []).map((p) => ({ x: p.x + dx, y: p.y + dy }));
                const pts = newPts.map((p) => `${p.x * w},${p.y * h}`).join(' ');
                (m.el as SVGPolygonElement).setAttribute('points', pts);
                const svg = (m.el as SVGPolygonElement).closest('svg');
                const lbl = svg?.querySelector<SVGTextElement>(`[data-label-for="${m.id}"]`);
                if (lbl) {
                    const cx = newPts.reduce((s, p) => s + p.x, 0) / newPts.length * w;
                    const cy = newPts.reduce((s, p) => s + p.y, 0) / newPts.length * h;
                    lbl.setAttribute('x', String(cx));
                    lbl.setAttribute('y', String(cy));
                }
            } else if (m.type === 'rect') {
                const rx = (m.origX1 ?? 0) + dx;
                const ry = (m.origY1 ?? 0) + dy;
                m.el.setAttribute('x', String(rx * w));
                m.el.setAttribute('y', String(ry * h));
                const svg = (m.el as SVGRectElement).closest('svg');
                const lbl = svg?.querySelector<SVGTextElement>(`[data-label-for="${m.id}"]`);
                if (lbl) {
                    const rw = parseFloat((m.el as SVGRectElement).getAttribute('width') || '0');
                    const rh = parseFloat((m.el as SVGRectElement).getAttribute('height') || '0');
                    lbl.setAttribute('x', String(rx * w + rw / 2));
                    lbl.setAttribute('y', String(ry * h + rh / 2));
                }
            }
        };

        const onUp = () => {
            const m = moveDragRef.current;
            if (!m) return;
            moveDragRef.current = null;
            const layers = pdfLayersRef.current;
            const w = dimension.width;
            const h = dimension.height;
            if (!layers || !w || !h) return;

            const onMove = onHighlightMoveRef?.current;
            if (onMove && highlightsRef) {
                const previous = highlightsRef.current.find(highlight => highlight.id === m.id);
                let moved: Highlight | undefined;
                if (previous?.type === 'positioned' && m.type === 'positioned') {
                    const element = m.el;
                    moved = { ...previous, x: parseFloat(element.style.left) / w, y: parseFloat(element.style.top) / h };
                } else if (previous?.type === 'polygon' && m.type === 'polygon') {
                    const points = (m.el.getAttribute('points') ?? '').split(' ').map(pair => {
                        const [px, py] = pair.split(',').map(Number);
                        return { x: px / w, y: py / h };
                    });
                    moved = { ...previous, points };
                } else if (previous?.type === 'rect' && m.type === 'rect') {
                    const x1 = parseFloat(m.el.getAttribute('x') ?? '0') / w;
                    const y1 = parseFloat(m.el.getAttribute('y') ?? '0') / h;
                    moved = { ...previous, x1, y1,
                        x2: x1 + parseFloat(m.el.getAttribute('width') ?? '0') / w,
                        y2: y1 + parseFloat(m.el.getAttribute('height') ?? '0') / h };
                }
                const geometry = moved ? highlightToGeometry(moved) : undefined;
                const previousGeometry = previous ? highlightToGeometry(previous) : undefined;
                if (moved && geometry && previousGeometry) {
                    const updated = highlightsRef.current.map(highlight => highlight.id === m.id ? moved : highlight);
                    highlightsRef.current = updated;
                    setHighlights(updated);
                    // Побочный эффект выполняется вне React updater ровно один раз на mouseup.
                    onMove(m.id, previousGeometry, geometry);
                }
                setActivePopup(null);
                document.body.style.cursor = activeModeRef.current === 'move' ? 'move' : '';
                document.body.style.userSelect = '';
                return;
            }

            const scheduleSave = (updated: any[]) => {
                if (moveSaveDebounceRef.current) clearTimeout(moveSaveDebounceRef.current);
                moveSaveDebounceRef.current = setTimeout(() => {
                    onSaveRef?.current?.(updated);
                }, 1000);
            };

            if (m.type === 'positioned') {
                const el = m.el as HTMLElement;
                const x = parseFloat(el.style.left) / w;
                const y = parseFloat(el.style.top) / h;
                setHighlights((prev: any[]) => {
                    const updated = prev.map((hl: any) =>
                        hl.id === m.id && hl.type === 'positioned' ? { ...hl, x, y } : hl
                    );
                    scheduleSave(updated);
                    return updated;
                });
            } else if (m.type === 'polygon') {
                const el = m.el as SVGPolygonElement;
                const ptsStr = el.getAttribute('points') || '';
                const pts = ptsStr.split(' ').map((s) => {
                    const [px, py] = s.split(',').map(Number);
                    return { x: px / w, y: py / h };
                });
                setHighlights((prev: any[]) => {
                    const updated = prev.map((hl: any) =>
                        hl.id === m.id && hl.type === 'polygon' ? { ...hl, points: pts } : hl
                    );
                    scheduleSave(updated);
                    return updated;
                });
            } else if (m.type === 'rect') {
                const el = m.el as SVGRectElement;
                const x1 = parseFloat(el.getAttribute('x') || '0') / w;
                const y1 = parseFloat(el.getAttribute('y') || '0') / h;
                const rw = parseFloat(el.getAttribute('width') || '0') / w;
                const rh = parseFloat(el.getAttribute('height') || '0') / h;
                setHighlights((prev: any[]) => {
                    const updated = prev.map((hl: any) =>
                        hl.id === m.id && hl.type === 'rect' ? { ...hl, x1, y1, x2: x1 + rw, y2: y1 + rh } : hl
                    );
                    scheduleSave(updated);
                    return updated;
                });
            }
            setActivePopup(null);
            document.body.style.cursor = activeModeRef.current === 'move' ? 'move' : '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [dimension, pdfLayersRef, pdfDocIdRef, onSaveRef, setHighlights, setActivePopup, onHighlightMoveRef, highlightsRef]);

    return { moveDragRef };
}
